import { Box, Grid, GridItem, Image, Show } from '@chakra-ui/react';
import { type FC, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { ReleaseIcon } from '@/assets/icons';
import { useReleaseStore } from '@/store';
import { env } from '@/utils';
import packageJson from '../../../package.json';
import FaceliftButton from '../Atoms/FaceliftButton';
import FaceliftDialog from '../Atoms/FaceliftDialog';
import FaceliftText from '../Atoms/FaceliftText';
import { useColorMode, useColorModeValue } from './color-mode-utils';
import { colors } from './theme';

type IReleaseProviderProps = {
  userId: string | null;
};
const baseURL = `${import.meta.env.VITE_NOTIFICATIONS_URL}`;

const ReleaseProvider: FC<IReleaseProviderProps> = ({ userId }) => {
  const nodeRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 20 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 20 });
  const [isDragging, setIsDragging] = useState(false);

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  //States for feature logic
  const { showPollingRelease, setPollingRelease } = useReleaseStore();
  const [dbVersion, setDbVersion] = useState(null);
  const [open, setOpen] = useState(false);

  const [countdown, setCountdown] = useState(300); // 5 minutes = 300 seconds
  const [isTimerStarted, setIsTimerStarted] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const boxShadow = useColorModeValue(
    '0 0 15px 5px rgba(17, 121, 100, 0.4)',
    '0 0 15px 5px rgba(17, 121, 100, 0.4)',
  );

  const gradient = useColorModeValue(
    `linear-gradient(180deg, ${colors.primary} 0%, ${colors.success} 100%)`,
    `linear-gradient(180deg, ${colors.success}0%, ${colors.primary} 100%)`,
  );

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    clearTimer(); // ✅ ensure no old timer exists
    setCountdown(300);
    setIsTimerStarted(true);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          clearTimer();
          window.location.replace(window.location.href);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ⏱ Format seconds to mm:ss
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  console.log('App Current Version', packageJson.version);

  useEffect(() => {
    if (!userId) return;
    // if (env === 'develop') return;
    const eventSource = new EventSource(`${baseURL}/polling-release/stream`, {
      withCredentials: false,
    });

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const dbVersion = data.application_version;
      if (dbVersion !== packageJson.version) {
        setPollingRelease(true);
        setDbVersion(dbVersion);
      } else {
        setPollingRelease(false);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE Error:', err);
      eventSource.close();
    };

    return () => eventSource.close();
  }, [userId, packageJson.version]);

  useEffect(() => {
    if (isTimerStarted && dbVersion) {
      // If version changes while timer active, restart it
      clearTimer();
      startTimer();
    }
    return () => clearTimer();
  }, [dbVersion]);

  //Reset the position on screen size change
  useEffect(() => {
    const setInitialPosition = () => {
      const initialX = window.innerWidth - 100; // 80 is button width
      const initialY = 140; // 20px down from top bar
      setPosition({ x: initialX, y: initialY });
      setStartPosition({ x: initialX, y: initialY });
    };

    // Set initial position on mount
    setInitialPosition();

    // Reset position on window resize
    window.addEventListener('resize', setInitialPosition);
    return () => window.removeEventListener('resize', setInitialPosition);
  }, []);

  return (
    <Box position={'relative'}>
      <Show when={showPollingRelease && dbVersion && userId}>
        <Draggable
          nodeRef={nodeRef}
          axis="both"
          bounds={{
            left: 0,
            top: 10,
            right: window.innerWidth - 80,
            bottom: window.innerHeight - 100,
          }}
          position={position}
          onStart={(e, data) => {
            setStartPosition({ x: data.x, y: data.y });
            setIsDragging(false);
          }}
          onDrag={() => setIsDragging(true)}
          onStop={(e, data) => {
            const withinYBounds = data.y > 2 && data.y < window.innerHeight - 120;
            if (withinYBounds) {
              if (data.x > window.innerWidth / 2) {
                setPosition({ x: window.innerWidth - 100, y: data.y });
              } else {
                setPosition({ x: 0, y: data.y });
              }
            } else {
              setPosition(startPosition);
            }
          }}
        >
          <Box
            ref={nodeRef}
            position="absolute"
            zIndex={9}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            cursor="grab"
            onClick={() => {
              if (!isDragging) {
                setOpen(true);
                setPollingRelease(false);
                startTimer();
              }
            }}
          >
            <Image
              src={isDark ? "/gifs/Waving-Hand-Drak.gif" : "/gifs/Waving-Hand-Light.gif"}
              width={12}
              height={12}
              borderRadius="50%"
              draggable={false}
            />
            <FaceliftButton fontSize="10px">Update Available</FaceliftButton>
          </Box>
        </Draggable>
      </Show>

      <FaceliftDialog
        open={open}
        onOpenChange={(open) => {
          if (!open) {
            setPollingRelease(false);
            setOpen(false);
          }
        }}
        sx={{
          maxWidth: '350px',
          maxHeight: '90vh',
          boxShadow: boxShadow,
          position: 'absolute',
          top: '9%',
          right: '2%',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          transition: 'top 0.2s ease, left 0.2s ease',
        }}
      >
        <Grid
          templateColumns={{
            base: '50px 1fr',
            sm: '54px 1fr',
            md: '54px 1fr',
            lg: '54px 1fr',
          }}
          alignItems="start"
          gap={2}
          mt={2}
        >
          <GridItem>
            <ReleaseIcon />
          </GridItem>

          <GridItem>
            <FaceliftText
              fontWeight="bold"
              fontSize="16px"
              _dark={{ WebkitTextFillColor: 'unset', color: 'unset' }}
              _light={{ WebkitTextFillColor: 'transparent', color: 'unset' }}
              style={{
                background: gradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
              }}
            >
              Update Available
            </FaceliftText>

            <FaceliftText
              mt={1}
              _dark={{ WebkitTextFillColor: 'unset', color: 'unset' }}
              _light={{ WebkitTextFillColor: 'transparent', color: 'unset' }}
              style={{
                textDecoration: 'none',
                background: gradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                transition: 'opacity 0.2s ease',
              }}
            >
              Version
              <span style={{ fontWeight: 'bold' }}>{` v${dbVersion} `}</span>
              is now live.&nbsp;
              <FaceliftText
                as="span"
                _dark={{ WebkitTextFillColor: 'unset', color: 'unset' }}
                _light={{ WebkitTextFillColor: 'transparent', color: 'unset' }}
                style={{
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  background: gradient,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  transition: 'opacity 0.2s ease',
                }}
                onClick={() => {
                  //This behaves like a hard refresh because it replaces the current history entry and reloads from the server.
                  window.location.replace(window.location.href);
                }}
              >
                {'Click to refresh '}
              </FaceliftText>
              and get the latest updates now.
            </FaceliftText>

            <FaceliftText
              mt={1}
              _dark={{ WebkitTextFillColor: 'unset', color: 'unset' }}
              _light={{ WebkitTextFillColor: 'transparent', color: 'unset' }}
              style={{
                textDecoration: 'none',
                background: gradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                transition: 'opacity 0.2s ease',
              }}
            >
              {'Auto Refresh in: '}
              <span
                style={{
                  fontWeight: 'bold',
                  background: gradient,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                }}
              >
                {formatTime(countdown)}
              </span>
            </FaceliftText>
          </GridItem>
        </Grid>
      </FaceliftDialog>
    </Box>
  );
};

export default ReleaseProvider;
